/**
 * useRealtimeAITrading - Real-time AI trading signal & agent activity streaming via WebSocket
 * Streams AI-generated signals, agent status changes, and performance updates
 */

'use client';

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useWebSocket } from '@/domains/market-data/hooks/useWebSocket';
import { JobEventCoalescer } from '../job-event-coalescer';

// =============================================================================
// TYPES
// =============================================================================

export interface RealtimeAISignal {
  signalId: string;
  agentId: string;
  agentName: string;
  symbol: string;
  action: 'buy' | 'sell' | 'hold' | 'close';
  confidence: number;
  strength: 'weak' | 'moderate' | 'strong' | 'very_strong';
  entryPrice?: number;
  targetPrice?: number;
  stopLoss?: number;
  reasoning: string;
  modelVersion: string;
  timestamp: string;
  expiresAt: string;
}

export interface RealtimeAgentStatus {
  agentId: string;
  name: string;
  status: 'active' | 'paused' | 'stopped' | 'error' | 'training';
  pnl: number;
  pnlPercent: number;
  openPositions: number;
  winRate: number;
  tradesCount: number;
  lastActivity: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  cpuUsage?: number;
  memoryUsage?: number;
  timestamp: string;
}

export interface RealtimeJobControlAck {
  type: 'job_control_ack'
  operation: 'pause_job' | 'resume_job' | 'stop_job' | 'live_create_job'
  jobId: string
  status: string
  timestamp?: string
}

export interface RealtimeAIPerformance {
  agentId: string;
  period: '1m' | '5m' | '15m' | '1h' | '1d';
  sharpe: number;
  sortino: number;
  maxDrawdown: number;
  profitFactor: number;
  totalReturn: number;
  winRate: number;
  avgWin: number;
  avgLoss: number;
  timestamp: string;
}

export interface UseRealtimeAITradingConfig {
  /** Auto-connect on mount */
  autoConnect?: boolean;
  /** Maximum signals to buffer */
  maxSignals?: number;
  /** Minimum confidence threshold */
  minConfidence?: number;
  /** Filter by agent IDs */
  agentIds?: string[];
  /** Callback on new signal */
  onSignal?: (signal: RealtimeAISignal) => void;
  /** Callback on agent status change */
  onAgentStatus?: (status: RealtimeAgentStatus) => void;
  /** Callback for explicit control acknowledgments; never coalesced with chatter. */
  onControlAck?: (ack: RealtimeJobControlAck) => void;
}

// =============================================================================
// HOOK
// =============================================================================

export const useRealtimeAITrading = (config: UseRealtimeAITradingConfig = {}) => {
  const {
    autoConnect = true,
    maxSignals = 100,
    minConfidence = 0,
    agentIds,
    onSignal,
    onAgentStatus,
    onControlAck,
  } = config;

  const [signals, setSignals] = useState<RealtimeAISignal[]>([]);
  const [agentStatuses, setAgentStatuses] = useState<Map<string, RealtimeAgentStatus>>(new Map());
  const [performance, setPerformance] = useState<Map<string, RealtimeAIPerformance>>(new Map());
  const [signalCount, setSignalCount] = useState(0);
  const [lastSignalTime, setLastSignalTime] = useState<Date | null>(null);

  const callbacksRef = useRef({ onSignal, onAgentStatus, onControlAck });
  callbacksRef.current = { onSignal, onAgentStatus, onControlAck };

  // Keep the latest status in state immediately, but batch callback emissions
  // so high-frequency JOB ticks do not become DESK chatter.
  const statusCoalescerRef = useRef<JobEventCoalescer<RealtimeAgentStatus> | null>(null);
  if (!statusCoalescerRef.current) {
    statusCoalescerRef.current = new JobEventCoalescer(30_000, ({ event }) => {
      callbacksRef.current.onAgentStatus?.(event);
    });
  }

  useEffect(() => () => statusCoalescerRef.current?.clear(), []);

  // Expire stale signals
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setSignals((prev: RealtimeAISignal[]) => {
        const filtered = prev.filter((s: RealtimeAISignal) => new Date(s.expiresAt) > now);
        return filtered.length !== prev.length ? filtered : prev;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleMessage = useCallback((data: any) => {
    if (data.type === 'pong' || data.type === 'ping') return;

    if (data.type === 'job_control_ack') {
      callbacksRef.current.onControlAck?.({
        ...data,
        type: 'job_control_ack',
      } as RealtimeJobControlAck);
      return;
    }

    switch (data.type) {
      case 'ai_signal': {
        const signal = data.data as RealtimeAISignal;

        // Filter by agent IDs
        if (agentIds?.length && !agentIds.includes(signal.agentId)) return;

        // Filter by confidence
        if (signal.confidence < minConfidence) return;

        // Skip expired signals
        if (new Date(signal.expiresAt) < new Date()) return;

        setSignals((prev: RealtimeAISignal[]) => [signal, ...prev].slice(0, maxSignals));
        setSignalCount((prev: number) => prev + 1);
        setLastSignalTime(new Date());

        callbacksRef.current.onSignal?.(signal);
        break;
      }

      case 'agent_status': {
        const status = data.data as RealtimeAgentStatus;

        // Filter by agent IDs
        if (agentIds?.length && !agentIds.includes(status.agentId)) return;

        setAgentStatuses((prev: Map<string, RealtimeAgentStatus>) => {
          const updated = new Map(prev);
          updated.set(status.agentId, status);
          return updated;
        });

        statusCoalescerRef.current?.push(`${status.agentId}:status`, status);
        break;
      }

      case 'ai_performance': {
        const perf = data.data as RealtimeAIPerformance;
        setPerformance((prev: Map<string, RealtimeAIPerformance>) => {
          const updated = new Map(prev);
          updated.set(perf.agentId, perf);
          return updated;
        });
        break;
      }
    }
  }, [agentIds, minConfidence, maxSignals]);

  // Private channel: mint short-lived ticket on every connect/reconnect
  const { state, send, connect, disconnect, subscribe } = useWebSocket(
    {
      channel: 'notifications',
      useTicket: true,
      requireTicket: true,
      softFailAuth: false,
      reconnect: { enabled: true, maxAttempts: 10, delay: 1000, backoffMultiplier: 1.5 },
      heartbeat: { enabled: true, interval: 20000, message: { type: 'ping' } },
    },
    handleMessage
  );

  const connectRef = useRef(connect);
  const disconnectRef = useRef(disconnect);
  connectRef.current = connect;
  disconnectRef.current = disconnect;

  // Auto-connect once; do not re-run when connect/disconnect identities change.
  useEffect(() => {
    if (autoConnect) {
      connectRef.current();
    }
    return () => disconnectRef.current();
  }, [autoConnect]);

  const agentIdsKey = agentIds?.join(',') ?? '';

  // Subscribe to AI channels when connected
  useEffect(() => {
    if (state.status === 'connected') {
      subscribe('ai_signal', () => { });
      subscribe('agent_status', () => { });
      subscribe('ai_performance', () => { });

      send({
        type: 'subscribe',
        channels: ['ai_signals', 'agent_status', 'ai_performance'],
        ...(agentIds?.length ? { agentIds } : {}),
      });
    }
  }, [state.status, subscribe, send, agentIds, agentIdsKey]);

  // Derived state — memoized so callers can safely depend on agentStatuses
  const agentStatusesArray = useMemo(
    () => Array.from(agentStatuses.values()) as RealtimeAgentStatus[],
    [agentStatuses]
  );
  const activeAgents = useMemo(
    () => agentStatusesArray.filter((a: RealtimeAgentStatus) => a.status === 'active'),
    [agentStatusesArray]
  );
  const errorAgents = useMemo(
    () => agentStatusesArray.filter((a: RealtimeAgentStatus) => a.status === 'error'),
    [agentStatusesArray]
  );
  const performanceArray = useMemo(
    () => Array.from(performance.values()) as RealtimeAIPerformance[],
    [performance]
  );

  const buySignals = useMemo(
    () => signals.filter((s: RealtimeAISignal) => s.action === 'buy'),
    [signals]
  );
  const sellSignals = useMemo(
    () => signals.filter((s: RealtimeAISignal) => s.action === 'sell'),
    [signals]
  );
  const strongSignals = useMemo(
    () =>
      signals.filter(
        (s: RealtimeAISignal) => s.strength === 'strong' || s.strength === 'very_strong'
      ),
    [signals]
  );

  const totalPnl = useMemo(
    () => agentStatusesArray.reduce((sum: number, a: RealtimeAgentStatus) => sum + a.pnl, 0),
    [agentStatusesArray]
  );
  const avgWinRate = useMemo(
    () =>
      agentStatusesArray.length > 0
        ? agentStatusesArray.reduce((sum: number, a: RealtimeAgentStatus) => sum + a.winRate, 0) /
        agentStatusesArray.length
        : 0,
    [agentStatusesArray]
  );

  const clearSignals = useCallback(() => {
    setSignals([]);
    setSignalCount(0);
    setLastSignalTime(null);
  }, []);

  return {
    // Connection
    connectionStatus: state.status,
    isConnected: state.status === 'connected',
    connect,
    disconnect,

    // Signals
    signals,
    buySignals,
    sellSignals,
    strongSignals,
    signalCount,
    lastSignalTime,

    // Agents
    agentStatuses: agentStatusesArray,
    activeAgents,
    errorAgents,
    totalPnl,
    avgWinRate,

    // Performance
    performance: performanceArray,

    // Actions
    clearSignals,
    send,
  };
};

export default useRealtimeAITrading;
